import axios from 'axios';
import { toast } from 'react-toastify';

// ---- action types ----------------------------------------------------
export const VENUES_REQUEST   = 'VENUES_REQUEST';
export const VENUES_SUCCESS   = 'VENUES_SUCCESS';
export const VENUES_FAILURE   = 'VENUES_FAILURE';

export const LAYOUTS_REQUEST  = 'LAYOUTS_REQUEST';
export const LAYOUTS_SUCCESS  = 'LAYOUTS_SUCCESS';
export const LAYOUTS_FAILURE  = 'LAYOUTS_FAILURE';

export const LAYOUT_DETAIL_REQUEST = 'LAYOUT_DETAIL_REQUEST';
export const LAYOUT_DETAIL_SUCCESS = 'LAYOUT_DETAIL_SUCCESS';
export const LAYOUT_DETAIL_FAILURE = 'LAYOUT_DETAIL_FAILURE';

export const VENUE_MUTATION_PENDING = 'VENUE_MUTATION_PENDING';
export const VENUE_MUTATION_DONE    = 'VENUE_MUTATION_DONE';

// ---- action creators -------------------------------------------------
const requestVenues   = ()      => ({ type: VENUES_REQUEST });
const receiveVenues   = (venues) => ({ type: VENUES_SUCCESS, payload: { venues } });
const failVenues      = (err)   => ({ type: VENUES_FAILURE, payload: err });

const requestLayouts  = (venueId) => ({ type: LAYOUTS_REQUEST, payload: { venueId } });
const receiveLayouts  = (venueId, layouts) => ({ type: LAYOUTS_SUCCESS, payload: { venueId, layouts } });
const failLayouts     = (err)   => ({ type: LAYOUTS_FAILURE, payload: err });

const requestLayoutDetail = (layoutId) => ({ type: LAYOUT_DETAIL_REQUEST, payload: { layoutId } });
const receiveLayoutDetail = (layout)   => ({ type: LAYOUT_DETAIL_SUCCESS, payload: { layout } });
const failLayoutDetail    = (err)      => ({ type: LAYOUT_DETAIL_FAILURE, payload: err });

const mutationPending = () => ({ type: VENUE_MUTATION_PENDING });
const mutationDone    = () => ({ type: VENUE_MUTATION_DONE });

// ---- thunks ----------------------------------------------------------

export function loadVenues(includeInactive = false) {
    return async (dispatch) => {
        dispatch(requestVenues());
        try {
            const { data } = await axios.get(`/api/venue/list?includeInactive=${includeInactive ? 'true' : 'false'}`);
            if (data.status === 'Ok') {
                dispatch(receiveVenues(data.venues || []));
            } else {
                toast.error(data.message || 'Failed to load venues');
                dispatch(failVenues(data.message));
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err.message || 'Failed to load venues';
            toast.error(msg);
            dispatch(failVenues(msg));
        }
    };
}

export function createVenue(model) {
    return async (dispatch) => {
        dispatch(mutationPending());
        try {
            const { data } = await axios.post('/api/venue/create', model);
            if (data.status === 'Ok') {
                toast.success('Venue created');
                await dispatch(loadVenues());
                return data.venueId;
            }
            toast.error(data.message || 'Failed to create venue');
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Failed to create venue');
        } finally {
            dispatch(mutationDone());
        }
        return null;
    };
}

export function updateVenue(model) {
    return async (dispatch) => {
        dispatch(mutationPending());
        try {
            const { data } = await axios.post('/api/venue/update', model);
            if (data.status === 'Ok') {
                toast.success('Venue updated');
                await dispatch(loadVenues());
                return true;
            }
            toast.error(data.message || 'Failed to update venue');
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Failed to update venue');
        } finally {
            dispatch(mutationDone());
        }
        return false;
    };
}

export function deactivateVenue(venueId) {
    return async (dispatch) => {
        dispatch(mutationPending());
        try {
            const { data } = await axios.delete(`/api/venue/${venueId}`);
            if (data.status === 'Ok') {
                toast.success('Venue deactivated');
                await dispatch(loadVenues());
                return true;
            }
            toast.error(data.message || 'Failed to deactivate venue');
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Failed to deactivate venue');
        } finally {
            dispatch(mutationDone());
        }
        return false;
    };
}

export function loadLayouts(venueId, includeInactive = false) {
    return async (dispatch) => {
        if (!venueId) return;
        dispatch(requestLayouts(venueId));
        try {
            const { data } = await axios.get(
                `/api/venue/${venueId}/layouts?includeInactive=${includeInactive ? 'true' : 'false'}`
            );
            if (data.status === 'Ok') {
                dispatch(receiveLayouts(venueId, data.layouts || []));
            } else {
                toast.error(data.message || 'Failed to load layouts');
                dispatch(failLayouts(data.message));
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err.message || 'Failed to load layouts';
            toast.error(msg);
            dispatch(failLayouts(msg));
        }
    };
}

export function loadLayoutDetail(layoutId) {
    return async (dispatch) => {
        if (!layoutId) return null;
        dispatch(requestLayoutDetail(layoutId));
        try {
            const { data } = await axios.get(`/api/venue/layout/${layoutId}`);
            if (data.status === 'Ok') {
                dispatch(receiveLayoutDetail(data.layout));
                return data.layout;
            }
            toast.error(data.message || 'Failed to load layout');
            dispatch(failLayoutDetail(data.message));
        } catch (err) {
            const msg = err?.response?.data?.message || err.message || 'Failed to load layout';
            toast.error(msg);
            dispatch(failLayoutDetail(msg));
        }
        return null;
    };
}

export function createLayout(model) {
    return async (dispatch) => {
        dispatch(mutationPending());
        try {
            const { data } = await axios.post('/api/venue/layout/create', model);
            if (data.status === 'Ok') {
                toast.success('Layout created');
                if (model.VenueId) await dispatch(loadLayouts(model.VenueId, true));
                return data.layoutId;
            }
            toast.error(data.message || 'Failed to create layout');
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Failed to create layout');
        } finally {
            dispatch(mutationDone());
        }
        return null;
    };
}

export function updateLayout(model) {
    return async (dispatch) => {
        dispatch(mutationPending());
        try {
            const { data } = await axios.post('/api/venue/layout/update', model);
            if (data.status === 'Ok') {
                toast.success('Layout saved');
                if (model.VenueId) await dispatch(loadLayouts(model.VenueId, true));
                return true;
            }
            toast.error(data.message || 'Failed to save layout');
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Failed to save layout');
        } finally {
            dispatch(mutationDone());
        }
        return false;
    };
}

export function deactivateLayout(layoutId, venueId) {
    return async (dispatch) => {
        dispatch(mutationPending());
        try {
            const { data } = await axios.delete(`/api/venue/layout/${layoutId}`);
            if (data.status === 'Ok') {
                toast.success('Layout deactivated');
                if (venueId) await dispatch(loadLayouts(venueId, true));
                return true;
            }
            toast.error(data.message || 'Failed to deactivate layout');
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Failed to deactivate layout');
        } finally {
            dispatch(mutationDone());
        }
        return false;
    };
}

// ---- station selection (used by Stations page enhancement) ----------

export async function fetchStationLayoutSelection(stationId) {
    if (!stationId) return null;
    try {
        const { data } = await axios.get(`/api/venue/selection/${stationId}`);
        if (data.status === 'Ok') return data.selection;
    } catch (err) {
        toast.error(err?.response?.data?.message || err.message || 'Failed to load station layout selection');
    }
    return null;
}

export async function saveStationLayoutSelection(selection) {
    try {
        const { data } = await axios.post('/api/venue/selection', selection);
        if (data.status === 'Ok') return true;
        toast.error(data.message || 'Failed to save station layout selection');
    } catch (err) {
        toast.error(err?.response?.data?.message || err.message || 'Failed to save station layout selection');
    }
    return false;
}

// ---- Fabric → POS layout import (additive; Admin opt-in only) ----------

export async function listEventSeatings(posVenueId) {
    if (!posVenueId) return [];
    try {
        const { data } = await axios.get(`/api/venue/${posVenueId}/event-seatings`);
        if (data.status === 'Ok') return data.seatings || [];
        toast.error(data.message || 'Failed to list event seatings');
    } catch (err) {
        toast.error(err?.response?.data?.message || err.message || 'Failed to list event seatings');
    }
    return [];
}

export async function previewLayoutImport(posVenueId, venueSeatingId) {
    if (!posVenueId) return null;
    try {
        const { data } = await axios.post(`/api/venue/${posVenueId}/layouts/import-preview`, {
            VenueSeatingId: venueSeatingId || null
        });
        if (data.status === 'Ok') return data;
        toast.error(data.message || 'Import preview failed');
    } catch (err) {
        toast.error(err?.response?.data?.message || err.message || 'Import preview failed');
    }
    return null;
}

export function commitLayoutImport(posVenueId, payload) {
    return async (dispatch) => {
        if (!posVenueId) return null;
        dispatch(mutationPending());
        try {
            const { data } = await axios.post(`/api/venue/${posVenueId}/layouts/import`, {
                VenueSeatingId: payload?.VenueSeatingId || null,
                TargetPosLayoutId: payload?.TargetPosLayoutId || null,
                LayoutName: payload?.LayoutName || null,
                Description: payload?.Description || null,
                ReplaceObjects: !!payload?.ReplaceObjects
            });
            if (data.status === 'Ok') {
                toast.success('Layout imported');
                await dispatch(loadLayouts(posVenueId, true));
                return data;
            }
            toast.error(data.message || 'Import failed');
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Import failed');
        } finally {
            dispatch(mutationDone());
        }
        return null;
    };
}
